import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface Subscription {
  id: string;
  creatorName: string;
  creatorAvatar?: string;
  tierName: string;
  amount: number;
  currency: string;
  status: 'active' | 'paused' | 'cancelled';
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/memberships`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptions(data.memberships || []);
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (subscriptionId: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/memberships/${subscriptionId}/pause`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Failed to pause subscription:', error);
    }
  };

  const handleResume = async (subscriptionId: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/memberships/${subscriptionId}/resume`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Failed to resume subscription:', error);
    }
  };

  const handleCancel = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to cancel this subscription?')) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/memberships/${subscriptionId}/cancel`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        fetchSubscriptions();
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">My Subscriptions</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your active creator subscriptions
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscriptions</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start supporting creators to see your subscriptions here.
          </p>
          <button
            onClick={() => navigate('/discover')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Discover Creators
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {subscriptions.map((sub) => (
            <div
              key={sub.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {sub.creatorAvatar ? (
                    <img
                      src={sub.creatorAvatar}
                      alt={sub.creatorName}
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserIcon className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{sub.creatorName}</h3>
                    <p className="text-sm text-gray-600">{sub.tierName}</p>
                    <div className="mt-2 flex items-center space-x-3">
                      <span className="flex items-center text-sm font-medium text-gray-900">
                        <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                        {sub.amount.toFixed(2)} {sub.currency.toUpperCase()}/month
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                          sub.status
                        )}`}
                      >
                        {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {sub.cancelAtPeriodEnd
                        ? `Cancels on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                        : `Renews on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  {sub.status === 'active' && !sub.cancelAtPeriodEnd && (
                    <>
                      <button
                        onClick={() => handlePause(sub.id)}
                        className="px-3 py-1 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        Pause
                      </button>
                      <button
                        onClick={() => handleCancel(sub.id)}
                        className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {sub.status === 'paused' && (
                    <button
                      onClick={() => handleResume(sub.id)}
                      className="px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Resume
                    </button>
                  )}
                  {sub.cancelAtPeriodEnd && (
                    <button
                      onClick={() => handleResume(sub.id)}
                      className="px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Reactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubscriptionsPage;
